import {
  Button,
  debounce,
  IconButton,
  IconButtonProps,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ConversationChannelType,
  ConversationParticipantEntity,
} from '@provision/conversation-types';
import { ConversationParticipation } from '@provision/entity-types';
import {
  ChangeEvent,
  forwardRef,
  SyntheticEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DynamicIcon,
  EllipsesMenu,
  EMAIL_VALIDATION_REGEX,
  Tooltip,
  WithTooltip,
} from 'src/components';
import { CreateEntityAutocomplete } from 'src/components/SearchBar/create-entity-autocomplete';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  appendDeletedClientTextToName,
  appendDisabledUserTextToName,
  Client,
  ClientEmail,
  Conversation,
  ConversationParticipantItem,
  isClientDeleted,
  useConversationParticipant,
} from 'src/graphql';
import { useSearch } from 'src/graphql/entities/search/use-search';
import { EntityNames } from 'src/graphql/types';
import useConversationViewsStore from 'src/store/conversation-views';
import { Modals, useShowModals } from 'src/store/modals';
import { useNotifications, useScrollPaginateHandler } from 'src/utils/hooks';

const WithIconButton = forwardRef(
  (
    {
      hideButton,
      children,
      ...props
    }: IconButtonProps & {
      hideButton?: boolean;
    },
    _ref,
  ) => {
    if (!hideButton) {
      return <IconButton {...props}>{children}</IconButton>;
    }

    return <Stack sx={{ padding: '0.3125rem' }}>{children}</Stack>;
  },
);
WithIconButton.displayName = 'WithIconButton';

export const ConversationParticipantInfo = ({
  participant,
  conversation,
  icon = '',
  text,
  extraInfo,
  isUserActive,
  onSuccessSubmit,
  readonly,
  toggleFollow,
  participations,
}: {
  participant: ConversationParticipantItem;
  conversation: Conversation;
  participations: ConversationParticipation[];
  icon?: string;
  text?: string;
  extraInfo?: string;
  isUserActive?: boolean;
  onSuccessSubmit?: () => void;
  readonly: boolean;
  toggleFollow?: (data: {
    emailParticipation?: ConversationParticipation | null;
    participant: ConversationParticipantItem;
  }) => void;
}) => {
  const theme = useTheme();
  const hideTooltip =
    readonly ||
    (participant.participantEntity?.toLowerCase() as ConversationParticipantEntity) !==
      ConversationParticipantEntity.Unknown;
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { showSuccessAlert } = useNotifications();

  const [searchTerm, setSearchTerm] = useState<null | string>('');

  const { associateParticipantWithClient } = useConversationParticipant();

  const [showModalMergeClient, showAddClientModal] = useShowModals(
    Modals.MergeClientModal,
    Modals.AddClientModal,
  );

  const emailParticipation = useMemo(() => {
    return participations.find((p) =>
      participant.conversationChannels.some(
        (channel) =>
          channel.id === p.channel.id &&
          (channel.conversationChannelTypeId as ConversationChannelType) ===
            ConversationChannelType.Email,
      ),
    );
  }, [participations, participant]);

  const { associatedAddressesPlaceholders } = useConversationViewsStore();

  const conversationAddress = useMemo(
    () =>
      conversation.address ??
      associatedAddressesPlaceholders[conversation.conversationId]?.[0],
    [associatedAddressesPlaceholders, conversation.address, conversation.conversationId],
  );

  const menuOptions = useMemo(() => {
    const isClient =
      (participant.participantEntity.toLowerCase() as ConversationParticipantEntity) ===
      ConversationParticipantEntity.Client;

    // A soft-deleted client must not be updatable or mergeable, so it offers no menu actions.
    if (isClient && !isClientDeleted(participant.client)) {
      return [
        {
          label: 'Update client',
          icon: 'edit',
          onClick: () => {
            showAddClientModal({ client: participant.client, address: conversationAddress });
          },
        },
        {
          icon: 'merge',
          label: 'Merge clients',
          onClick: () => {
            showModalMergeClient({ client: participant.client });
          },
        },
      ];
    }

    return [];
  }, [
    participant.participantEntity,
    participant.client,
    showAddClientModal,
    conversationAddress,
    showModalMergeClient,
  ]);

  const partnerIdsString = [
    conversation?.partnerId ?? participant.partnerId,
    PLATFORM_PARTNER_ID,
  ].join(',');

  const clientsFilter = useMemo(() => {
    return [
      // This filter should be present, since it is also used in multi-tenancy mode
      // when partner-id header is not included in the request.
      {
        attribute: 'partnerId',
        operator: 'in',
        value: partnerIdsString,
      },
    ];
  }, [partnerIdsString]);

  const {
    records: clientsToAdd,
    loadMore,
    hasMore,
    isLoading,
  } = useSearch({
    entityName: EntityNames.Client,
    searchTerm,
    filter: clientsFilter,
  }) ?? {};

  const filteredClientsToAdd = useMemo(
    () =>
      clientsToAdd
        // Remove duplicated elements
        ?.filter(
          (client, index, arr) =>
            arr.findIndex((c) => c.clientId === client.clientId) === index,
        )
        .map((client) => ({
          label: client.fullName,
          ...client,
        })) || [],
    [clientsToAdd],
  );

  const handleScroll = useScrollPaginateHandler({
    loadMore,
    enabled: hasMore,
    thresholdPx: 100,
  });

  const autocompleteInputRef = useRef<HTMLElement | null>(null);

  const [isAddEntityOpen, setIsAddEntityOpen] = useState(false);

  const onAutocompleteChange = useCallback(
    async (_: SyntheticEvent, newValue: Partial<Client> | null) => {
      if (!newValue) {
        return;
      }

      const response = await associateParticipantWithClient({
        participant,
        client: newValue,
        partnerId: participant.partnerId ?? conversation?.partnerId,
      });

      if (!response) {
        return;
      }

      setSearchTerm('');
      onSuccessSubmit?.();

      showSuccessAlert('Participant was associated with the client');
    },
    [
      associateParticipantWithClient,
      conversation?.partnerId,
      onSuccessSubmit,
      participant,
      showSuccessAlert,
    ],
  );

  const onTextFieldChange = useMemo(
    () =>
      debounce(({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchTerm(target.value);
      }, SEARCH_DEBOUNCE_DELAY),
    [],
  );

  const onCreateButtonClick = useCallback(() => {
    const emails: ClientEmail[] = [];

    if (participant.participantIdentity.match(EMAIL_VALIDATION_REGEX)) {
      emails.push({
        email: participant.participantIdentity,
      } as ClientEmail);
    }

    showAddClientModal({
      client: {
        emails,
      },
      participant,
      onSuccessSubmit,
    });
  }, [onSuccessSubmit, participant, showAddClientModal]);

  const showMenu = isMenuOpen && menuOptions?.length && !readonly;

  // A soft-deleted client stays visible as a participant (it is fetched with `includeDeleted`),
  // but is labelled so agents can tell it has been deleted.
  let participantDisplayName = text;
  if (participant.client?.deletedAt) {
    participantDisplayName = appendDeletedClientTextToName(text);
  } else if (!isUserActive) {
    participantDisplayName = appendDisabledUserTextToName(text);
  }

  return (
    <Stack
      direction="row"
      gap={1}
      alignItems="center"
      onMouseEnter={() => {
        setIsMenuOpen(true);
      }}
      onMouseLeave={() => {
        setIsMenuOpen(false);
      }}
    >
      <Stack
        width={isAddEntityOpen ? 0 : 'auto'}
        overflow="hidden"
        justifyContent="space-between"
        sx={{
          transition: '0.2s width',
          flex: '0 0 auto',
        }}
      >
        <WithTooltip title="Add client" hideTooltip={hideTooltip}>
          <WithIconButton
            size="small"
            hideButton={hideTooltip}
            onClick={() => {
              setIsAddEntityOpen((currentValue) => !currentValue);
              autocompleteInputRef.current?.focus();
            }}
          >
            <DynamicIcon
              name={icon}
              filled={!hideTooltip}
              sx={{
                fontSize: '1.4rem',
                width: '1.4rem',
                height: '1.4rem',
              }}
              color={!hideTooltip ? theme.palette.custom.OV.sys.secondary : undefined}
            />
          </WithIconButton>
        </WithTooltip>
      </Stack>
      <CreateEntityAutocomplete
        searchPlaceholder="Find & Associate Existing Client"
        inputRef={autocompleteInputRef}
        isAddEntityOpen={isAddEntityOpen}
        setIsAddEntityOpen={setIsAddEntityOpen}
        filteredEntities={filteredClientsToAdd}
        handleScroll={handleScroll}
        onAutocompleteChange={onAutocompleteChange}
        onAutocompleteTextFieldChange={onTextFieldChange}
        isLoading={isLoading}
        createButtonText="+ Create Client"
        onCreateButtonClick={onCreateButtonClick}
      />
      <Stack
        direction="row"
        gap={0.5}
        alignItems="center"
        width={isAddEntityOpen ? 0 : 'auto'}
        justifyContent="flex-start"
        sx={{
          transition: '0.2s width',
          maxWidth: '70%',
          overflow: 'hidden',
        }}
      >
        <Typography
          minWidth="30%"
          variant="body2"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {participantDisplayName}
        </Typography>

        {!!extraInfo && <Typography variant="body2">({extraInfo})</Typography>}

        {!!toggleFollow && (
          <Tooltip title="Followers will receive email notifications about new messages in this conversation. Agent email addresses will not be exposed. Replies to these notifications will be treated as public replies.">
            <Button
              variant="text"
              sx={{ fontSize: '0.725rem', width: 'fit-content' }}
              onClick={() => toggleFollow({ emailParticipation, participant })}
            >
              {emailParticipation?.isActive ? 'Remove as Follower' : 'Set to Follower'}
            </Button>
          </Tooltip>
        )}
      </Stack>
      {showMenu ? <EllipsesMenu options={menuOptions} /> : null}
    </Stack>
  );
};
